/**
 * Confirmation contract for the dedicated "Delete account" page.
 *
 * To proceed with the (irreversible-after-grace-period) deletion the user must
 * re-type the EXACT email of the account being deleted into a confirmation box.
 * This is a deliberate friction step against ACCIDENTAL deletion and against
 * confusing "Delete account" with "Sign out". It is NOT a security boundary —
 * the real authorization is the session cookie the backend checks on
 * `requestAccountDeletion`; this only gates the destructive button in the UI.
 *
 * Matching is normalized with trim + lowercase (case-insensitive):
 *   - Email domains are case-insensitive by spec, and every mainstream provider
 *     also treats the local-part case-insensitively, so requiring exact case
 *     would only produce false negatives that frustrate a legitimate user.
 *   - A leading/trailing space from autofill or copy-paste should not block a
 *     correct confirmation.
 * The trade-off (someone could type a different-cased variant) is irrelevant:
 * this is an anti-mistake gate, not authentication.
 */
export function normalizeEmailForConfirmation(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Returns `true` only when `input` matches the account email under the
 * normalization rules above. An empty/absent account email never matches, so a
 * missing session can never accidentally enable the destructive button.
 */
export function emailMatchesAccount(
  input: string,
  accountEmail: string | null | undefined,
): boolean {
  if (!accountEmail) {
    return false;
  }
  const normalizedAccount = normalizeEmailForConfirmation(accountEmail);
  if (normalizedAccount === "") {
    return false;
  }
  return normalizeEmailForConfirmation(input) === normalizedAccount;
}

/** Suffix of the dedicated deletion route (the per-tab `/u/<n>` prefix varies). */
const DELETE_ACCOUNT_PATH_SUFFIX = "/profile/delete-account";

/** `true` when `pathname` still points at the dedicated deletion page. */
export function isDeleteAccountPath(pathname: string): boolean {
  return pathname.endsWith(DELETE_ACCOUNT_PATH_SUFFIX);
}

/**
 * Guard for the "a guest has no account to delete" redirect.
 *
 * A guest must be bounced off the deletion page to the profile — but the
 * redirect has to be issued AT MOST ONCE, and this predicate is what makes that
 * true.
 *
 * Why it is not a stylistic guard: `useSession().isVisitor()` is reactive on
 * `activePathname()` (`isVisitor` → `resolvedData` → `activeView` →
 * `activePathname`, because the per-tab account is parsed out of the `/u/<n>`
 * URL prefix). So an effect shaped like «if visitor → navigate» READS the
 * pathname and then CHANGES the pathname: the navigation re-triggers the very
 * effect that issued it, and `isVisitor()` is still `true` at the destination
 * (a guest stays a guest), so the effect navigates AGAIN. That is a
 * self-feeding redirect loop.
 *
 * Measured in this app before this guard existed (instrumented
 * `history.replaceState` on a guest hitting `/profile/delete-account`): the
 * redirect was emitted TWICE — `replaceState -> /u/0/profile` back to back.
 * The loop stopped at the second pass only by accident: `activePathname()` is a
 * plain signal with default `===` equality, so re-navigating to the SAME
 * pathname does not notify and the feedback dies there. The tab did NOT hang
 * here (`load` fired, rAF kept ticking) — but nothing in this file's contract
 * guaranteed that. The moment the redirect target stops being a fixed point
 * (e.g. `/profile` gains a guest redirect of its own, or the destination gains
 * a query/hash), the same shape becomes an unbounded synchronous Solid update
 * loop that never yields the main thread and leaves the deletion page BLANK
 * forever — which is what it does in the sibling app this guard is ported from.
 * Account deletion is a user right and a store requirement, and a blank page
 * shows no error: it just looks like it is loading, forever.
 *
 * Anchoring the redirect to «we are STILL on the deletion page» closes the loop
 * at its source: once the first navigation lands on `/profile`, the condition is
 * false and no second navigation is ever issued.
 *
 * `isPending` is honoured first so an in-flight session verification never
 * expels a real signed-in user mid-boot.
 */
export function shouldRedirectGuestAwayFromDeletion(input: {
  isPending: boolean;
  isVisitor: boolean;
  pathname: string;
}): boolean {
  if (input.isPending) {
    return false;
  }
  if (!input.isVisitor) {
    return false;
  }
  return isDeleteAccountPath(input.pathname);
}
